import type { SupplierRiskEmailFormState } from '../../utils/supplierRiskEmailSettings'

type Props = {
  value: SupplierRiskEmailFormState
  onChange: (next: SupplierRiskEmailFormState) => void
}

function Field({
  label,
  hint,
  value,
  onChange,
  placeholder,
}: {
  label: string
  hint?: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {hint ? <p className="text-xs text-gray-500 mb-1">{hint}</p> : null}
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
      />
    </div>
  )
}

/** Editable To/CC + reminder days for supplier risk intake & alert feedback */
export default function SupplierRiskEmailSettingsFields({ value, onChange }: Props) {
  const set = (patch: Partial<SupplierRiskEmailFormState>) => onChange({ ...value, ...patch })

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-bold text-gray-800">Supplier risk email routing</h4>
        <p className="text-xs text-gray-500 mt-1">
          Comma-separated addresses. Reminders send only on the days listed (default Day 3 and Day 5).
        </p>
      </div>

      <Field
        label="From"
        value={value.fromEmail}
        onChange={(fromEmail) => set({ fromEmail })}
        placeholder="Early Signals <noreply@theearlysignals.com>"
      />

      <div className="rounded-lg border border-gray-200 p-3 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Risk intake — on submit</p>
        <Field
          label="To"
          hint="TPRM Governance only — Approve/Reject links go here (not requestor or CM)"
          value={value.intakeSubmitTo}
          onChange={(intakeSubmitTo) => set({ intakeSubmitTo })}
          placeholder="tprm@example.com"
        />
        <Field
          label="CC"
          hint="Optional ops CC on the approval-request email (never the requestor)"
          value={value.intakeSubmitCc}
          onChange={(intakeSubmitCc) => set({ intakeSubmitCc })}
        />
      </div>

      <div className="rounded-lg border border-gray-200 p-3 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Risk intake — on approve / reject (TPRM copy)
        </p>
        <Field
          label="To"
          hint="TPRM gets a copy of approved and rejected decisions; requestor is emailed separately"
          value={value.intakeApprovedTo}
          onChange={(intakeApprovedTo) => set({ intakeApprovedTo })}
        />
        <Field
          label="CC"
          value={value.intakeApprovedCc}
          onChange={(intakeApprovedCc) => set({ intakeApprovedCc })}
        />
      </div>

      <div className="rounded-lg border border-gray-200 p-3 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Risk alert feedback</p>
        <Field
          label="Day 1 alert CC"
          hint="Copied on the initial supplier risk alert to category managers"
          value={value.alertDay1Cc}
          onChange={(alertDay1Cc) => set({ alertDay1Cc })}
        />
        <Field
          label="On feedback submit — To"
          value={value.alertOnSubmitTo}
          onChange={(alertOnSubmitTo) => set({ alertOnSubmitTo })}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field
            label="Reminder days"
            hint="e.g. 3, 5 — form on first day, reminder on later days"
            value={value.remindOnDays}
            onChange={(remindOnDays) => set({ remindOnDays })}
            placeholder="3, 5"
          />
          <Field
            label="Due by day"
            value={value.dueByDay}
            onChange={(dueByDay) => set({ dueByDay })}
            placeholder="5"
          />
        </div>
      </div>
    </div>
  )
}
